import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import api from '../../services/api';

interface ForgotUsernameDialogProps {
  open: boolean;
  onClose: () => void;
}

const ForgotUsernameDialog = ({ open, onClose }: ForgotUsernameDialogProps) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // /forgot-username-all sends every username (personal + joint aliases) linked to the email
      await api.post('/api/Auth/forgot-username-all', { email: email.trim() });
      setSuccess(true);
    } catch (err: any) {
      setError('Failed to process request. Please try again.');
      console.error('Forgot username error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forgot Username</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            If one or more accounts are associated with this email, we've sent you a list of
            all usernames linked to it — including any joint or business account aliases.
            Please check your inbox and spam folder.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 1 }}>
              Enter your email address and we'll send you a reminder of <strong>all usernames</strong> associated
              with it, including personal accounts and any joint or business account aliases.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              autoFocus
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Username List'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotUsernameDialog;
